/**
 * Cash-to-cash FX: the approved rate representation, both input modes and exact half-up cents.
 *
 * Sources, quoted rather than re-decided here:
 *
 *   - `DECISION-REGISTER.md` "FX precision" (the operator's approved value, 2026-10-15): supplied
 *     rate as **plain positive decimal text with at most 12 integer and 12 fractional digits**,
 *     exact coefficient `numeric(24,0)` and scale `0..12`, **retaining the input text**; destination
 *     mode derives the exact positive rational `destinationMagnitude/sourceMagnitude` on read;
 *     display the derived rate to **six fractional places half-up** with an approximation marker;
 *     native legs preserved, half-up destination cents, and repeating display ratios never
 *     authoritative money.
 *   - `IMPLEMENTATION-CONTRACTS.md` §3: direction is **destination currency units per source unit**;
 *     rate mode preserves the source amount, the exact supplied rate and `halfUp(source × rate)`
 *     destination cents; destination mode preserves **both** authoritative native leg amounts and
 *     derives the rational on read, with **no duplicate parent ratio-money**; a destination outside
 *     the individual bound or rounded to zero is refused.
 *   - `REQUIREMENTS.md` FX-03/FX-05/FX-06/FX-07: explicit codes, both positions of the rate stored
 *     with the dated transfer and labeled destination-per-source; positive magnitudes; no assumed
 *     parity and no unconfirmed reuse; calculated destinations round nearest cent with exact
 *     half-cent ties up; the native amounts are never changed by anything a display does.
 *
 * Two properties are the whole point of this module:
 *
 *   - **money is only ever derived from a stated mode.** There is no default rate, no assumed
 *     parity (same currency is a refusal, not a rate of `1`), and no reuse of a previous pair's
 *     rate without a caller stating it again;
 *   - **a display never becomes money.** The six-place rate is a *rendering* of an exact rational;
 *     it is returned beside a marker saying whether six places captured it, and it is never an
 *     operand. A repeating ratio therefore cannot rewrite either leg.
 *
 * Nothing here reads a clock, a database or the network.
 */
import { MAX_MINOR_MAGNITUDE, isWithinIndividualBound } from './money.js';
/** The two ways a cross-currency pair states its money (register "FX precision"). */
export const FX_INPUT_MODES = ['rate', 'destination'];
/**
 * Supplied rate text: a canonical integer part of at most 12 digits (no leading zeros, so the
 * stored coefficient and scale reproduce the text exactly) and an optional 1..12-digit fraction.
 */
export const SUPPLIED_RATE_PATTERN = '^(?:0|[1-9][0-9]{0,11})(?:\\.[0-9]{1,12})?$';
const SUPPLIED_RATE = new RegExp(SUPPLIED_RATE_PATTERN);
/** At most 12 integer digits (`numeric(24,0)` minus the 12 fractional ones). */
export const MAX_RATE_INTEGER_DIGITS = 12;
/** At most 12 fractional digits; the stored scale is always `0..12`. */
export const MAX_RATE_SCALE = 12;
/** The derived rate is displayed to six fractional places, half-up (register). */
export const DERIVED_RATE_PLACES = 6;
export class InvalidFxError extends Error {
    name = 'InvalidFxError';
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
    }
}
function magnitudeOf(value) {
    return value < 0n ? -value : value;
}
function greatestCommonDivisor(left, right) {
    let a = magnitudeOf(left);
    let b = magnitudeOf(right);
    while (b !== 0n) {
        const remainder = a % b;
        a = b;
        b = remainder;
    }
    return a;
}
/** Reduce an exact ratio; a zero numerator keeps denominator `1` so the shape stays a ratio. */
export function reduceRatio(numerator, denominator) {
    if (denominator === 0n) {
        throw new InvalidFxError('FX_INPUT_INCOMPLETE', 'a rate needs a nonzero denominator');
    }
    if (numerator === 0n)
        return { numerator: 0n, denominator: 1n };
    const divisor = greatestCommonDivisor(numerator, denominator);
    const sign = denominator < 0n ? -1n : 1n;
    return { numerator: (sign * numerator) / divisor, denominator: (sign * denominator) / divisor };
}
/**
 * Divide two exact integers, rounding a **exact half-cent tie up** (away from zero) and never
 * through a float. `halfUpDivide(12n, 8n)` is `2n` — `1.5` becomes `2` — which is the register's
 * "exact half-cent ties up" and not JavaScript's banker's rounding.
 */
export function halfUpDivide(numerator, denominator) {
    if (denominator <= 0n) {
        throw new InvalidFxError('FX_INPUT_INCOMPLETE', 'the divisor must be positive');
    }
    const negative = numerator < 0n;
    const magnitude = magnitudeOf(numerator);
    const rounded = (2n * magnitude + denominator) / (2n * denominator);
    return negative ? -rounded : rounded;
}
/**
 * Parse the supplied rate text into its exact coefficient and scale.
 *
 * The canonical integer part is what makes "retaining the input text" true rather than hopeful:
 * `formatSuppliedRate` reconstructs the text from the two stored numbers alone, so the database
 * needs no second, textual copy that could drift from the numbers beside it.
 */
export function parseSuppliedRate(text) {
    if (typeof text !== 'string' || !SUPPLIED_RATE.test(text)) {
        throw new InvalidFxError('FX_RATE_MALFORMED', `not a plain positive decimal rate with at most ${MAX_RATE_INTEGER_DIGITS} integer and ` +
            `${MAX_RATE_SCALE} fractional digits: ${JSON.stringify(text)}`);
    }
    const fraction = /\.([0-9]+)$/.exec(text)?.[1] ?? '';
    const coefficient = BigInt(text.replace('.', ''));
    if (coefficient === 0n) {
        throw new InvalidFxError('FX_RATE_ZERO', 'a supplied rate must be greater than zero');
    }
    return { text, coefficient, scale: fraction.length };
}
/** Reconstruct the exact input text from the coefficient and scale that were stored. */
export function formatSuppliedRate(rate) {
    const divisor = 10n ** BigInt(rate.scale);
    const integer = rate.coefficient / divisor;
    if (rate.scale === 0)
        return integer.toString();
    const fraction = (rate.coefficient % divisor).toString().padStart(rate.scale, '0');
    return `${integer}.${fraction}`;
}
/**
 * The stored representation back into the text it was written as.
 *
 * The database keeps the coefficient (`numeric(24,0)`) and the scale and nothing textual, so this
 * is how a read reconstructs the supplied rate: one representation, no second copy to drift.
 */
export function suppliedRateFromCoefficient(coefficient, scale) {
    if (!Number.isInteger(scale) || scale < 0 || scale > MAX_RATE_SCALE) {
        throw new InvalidFxError('FX_RATE_MALFORMED', `the stored scale must be 0..${MAX_RATE_SCALE}`);
    }
    if (coefficient <= 0n || coefficient >= 10n ** 24n) {
        throw new InvalidFxError('FX_RATE_MALFORMED', 'the stored coefficient must be a positive integer of at most 24 digits');
    }
    const text = formatSuppliedRate({ text: '', coefficient, scale });
    const rate = { text, coefficient, scale };
    // The reconstructed text has to be a rate this module would accept again, or the row that
    // produced it is outside the approved representation and must be reported rather than read.
    return parseSuppliedRate(rate.text);
}
/** The exact ratio a supplied rate stands for: `coefficient / 10^scale`. */
export function ratioFromSuppliedRate(rate) {
    return reduceRatio(rate.coefficient, 10n ** BigInt(rate.scale));
}
/**
 * The exact ratio a destination amount stands for, in destination units per source unit:
 * `destinationMagnitude / sourceMagnitude` (register, "destination mode derives the exact positive
 * rational … on read"). Nothing is stored for it — no duplicate parent ratio-money.
 */
export function ratioFromDestination(sourceMinor, destinationMinor) {
    if (sourceMinor <= 0n) {
        throw new InvalidFxError('FX_SOURCE_NOT_POSITIVE', 'the source magnitude must be positive');
    }
    if (destinationMinor <= 0n) {
        throw new InvalidFxError('FX_DESTINATION_NOT_POSITIVE', 'the destination magnitude must be positive');
    }
    return reduceRatio(destinationMinor, sourceMinor);
}
/**
 * Render an exact ratio to `places` fractional digits, half-up, with the approximation marker.
 *
 * `approximate` is true exactly when the printed digits are **not** the ratio: for `1/3` the text
 * is `0.333333` and the marker is true, and for `3/2` the text is `1.500000` with the marker false.
 * The caller prints the marker beside the figure; it never feeds the figure anywhere.
 */
export function displayRatio(ratio, places = DERIVED_RATE_PLACES) {
    if (!Number.isInteger(places) || places < 0 || places > 12) {
        throw new InvalidFxError('FX_INPUT_INCOMPLETE', 'the display scale must be 0..12');
    }
    const scale = 10n ** BigInt(places);
    const scaled = halfUpDivide(ratio.numerator * scale, ratio.denominator);
    const integer = scaled / scale;
    const fraction = (scaled % scale).toString().padStart(places, '0');
    const approximate = scaled * ratio.denominator !== ratio.numerator * scale;
    return { text: `${integer}.${fraction}`, approximate };
}
/** The direction label: destination currency units per source currency unit (register, FX-05). */
export function fxDirectionLabel(sourceCurrency, destinationCurrency) {
    return `${destinationCurrency} per ${sourceCurrency}`;
}
/**
 * The destination the rate mode computes: `halfUp(source × coefficient / 10^scale)`, refused when
 * it leaves the individual money bound or rounds to zero. The source magnitude is an operand and is
 * never rewritten.
 */
export function destinationFromSource(sourceMinor, rate) {
    const destination = halfUpDivide(sourceMinor * rate.coefficient, 10n ** BigInt(rate.scale));
    if (destination === 0n) {
        throw new InvalidFxError('FX_DESTINATION_ROUNDS_TO_ZERO', 'the computed destination rounds to zero cents, so this rate moves no money');
    }
    if (!isWithinIndividualBound(destination)) {
        throw new InvalidFxError('FX_DESTINATION_OUT_OF_RANGE', `the computed destination exceeds the individual money bound (${MAX_MINOR_MAGNITUDE} cents)`);
    }
    return destination;
}
function requirePositiveSource(sourceMinor) {
    if (sourceMinor <= 0n) {
        throw new InvalidFxError('FX_SOURCE_NOT_POSITIVE', 'the source amount must be a positive magnitude');
    }
    if (!isWithinIndividualBound(sourceMinor)) {
        throw new InvalidFxError('FX_SOURCE_OUT_OF_RANGE', `the source amount exceeds the individual money bound (${MAX_MINOR_MAGNITUDE} cents)`);
    }
}
function requirePositiveDestination(destinationMinor) {
    if (destinationMinor <= 0n) {
        throw new InvalidFxError('FX_DESTINATION_NOT_POSITIVE', 'the destination amount must be a positive magnitude');
    }
    if (!isWithinIndividualBound(destinationMinor)) {
        throw new InvalidFxError('FX_DESTINATION_OUT_OF_RANGE', `the destination amount exceeds the individual money bound (${MAX_MINOR_MAGNITUDE} cents)`);
    }
}
/**
 * Plan one cross-currency cash-to-cash pair from exactly one stated mode.
 *
 * This is the only place a cross-currency destination is computed, and every refusal happens here —
 * before a caller can write a row. Same currency is a refusal rather than a rate of `1`: the
 * contract has no implicit parity, and no branch below reaches for one.
 */
export function planFxTransfer(input) {
    if (input.sourceCurrency === input.destinationCurrency) {
        throw new InvalidFxError('FX_SAME_CURRENCY', `both legs are ${input.sourceCurrency}: a cross-currency pair needs two currencies, and no ` +
            'parity rate is ever assumed');
    }
    requirePositiveSource(input.sourceMinor);
    const directionLabel = fxDirectionLabel(input.sourceCurrency, input.destinationCurrency);
    if (input.inputMode === 'rate') {
        if (input.rateText === undefined) {
            throw new InvalidFxError('FX_INPUT_INCOMPLETE', 'rate mode needs the supplied rate text');
        }
        if (input.destinationMinor !== undefined) {
            throw new InvalidFxError('FX_INPUT_INCOMPLETE', 'rate mode derives the destination: a supplied destination alongside a supplied rate ' +
                'would be a second, non-authoritative figure');
        }
        const suppliedRate = parseSuppliedRate(input.rateText);
        const destinationMinor = destinationFromSource(input.sourceMinor, suppliedRate);
        return {
            inputMode: 'rate',
            sourceCurrency: input.sourceCurrency,
            destinationCurrency: input.destinationCurrency,
            sourceMinor: input.sourceMinor,
            destinationMinor,
            suppliedRate,
            directionLabel,
            displayRate: displayRatio(ratioFromSuppliedRate(suppliedRate)),
        };
    }
    if (input.inputMode !== 'destination') {
        throw new InvalidFxError('FX_INPUT_INCOMPLETE', `unknown FX input mode: ${JSON.stringify(input.inputMode)}`);
    }
    if (input.destinationMinor === undefined) {
        throw new InvalidFxError('FX_INPUT_INCOMPLETE', 'destination mode needs the destination amount');
    }
    if (input.rateText !== undefined) {
        throw new InvalidFxError('FX_INPUT_INCOMPLETE', 'destination mode preserves the destination exactly: a supplied rate alongside it would be ' +
            'a second, non-authoritative figure');
    }
    requirePositiveDestination(input.destinationMinor);
    return {
        inputMode: 'destination',
        sourceCurrency: input.sourceCurrency,
        destinationCurrency: input.destinationCurrency,
        sourceMinor: input.sourceMinor,
        destinationMinor: input.destinationMinor,
        suppliedRate: null,
        directionLabel,
        displayRate: displayRatio(ratioFromDestination(input.sourceMinor, input.destinationMinor)),
    };
}
//# sourceMappingURL=fx.js.map